import {
  AsyncThunkPayloadCreator,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  PayloadAction,
} from '@reduxjs/toolkit'
import { ServiceAccount, ServiceAccountCreationPayload, ZapierProfile } from '@sendhub/integrations/api-client-typescript-axios'
import lodash from 'lodash'
import { integrationsApi } from '../../services/integrations-api'
import { fetchIntegrationConfigs } from '../integration-config/integration-configs.slice'
import { RootState } from '../root'
import { SENDHUB_BASE_URL } from '../../../utils/config'

export const SERVICE_ACCOUNTS_FEATURE_KEY = 'serviceAccounts'

export interface ServiceAccountsState extends EntityState<Readonly<ServiceAccount>> {
  loadingStatus: 'not loaded' | 'loading' | 'loaded' | 'error'
  error?: string | null
}

export const serviceAccountsAdapter = createEntityAdapter<Readonly<ServiceAccount>>()

/**
 * Export an effect using createAsyncThunk from
 * the Redux Toolkit: https://redux-toolkit.js.org/api/createAsyncThunk
 *
 * e.g.
 * ```
 * import React, { useEffect } from 'react';
 * import { useDispatch } from 'react-redux';
 *
 * // ...
 *
 * const dispatch = useDispatch();
 * useEffect(() => {
 *   dispatch(fetchServiceAccounts())
 * }, [dispatch]);
 * ```
 */
export const fetchServiceAccounts = createAsyncThunk('serviceAccounts/fetchStatus', async (_, thunkAPI) => {
  return integrationsApi.listServiceAccounts().then((r) => r.data)
})
export const createServiceAccount = createAsyncThunk(
  'serviceAccounts/createStatus',
  async (serviceAccount: ServiceAccountCreationPayload, thunkAPI) => {
    // TODO: implement RTK Query so we don't have to manually fetch after mutations
    const created = await integrationsApi.createServiceAccount(serviceAccount).then((r) => r.data)
    await thunkAPI.dispatch(fetchServiceAccounts())
    return created
  }
)
export const updateServiceAccount = createAsyncThunk(
  'serviceAccounts/updateStatus',
  async ({ id, serviceAccount }: { id: string; serviceAccount: ServiceAccountCreationPayload }, thunkAPI) => {
    return integrationsApi.updateServiceAccount(id, serviceAccount).then((r) => thunkAPI.dispatch(fetchServiceAccounts()))
  }
)
export const deleteServiceAccount = createAsyncThunk('serviceAccounts/deleteStatus', async (id: string, thunkAPI) => {
  const result = (await integrationsApi.deleteServiceAccount(id)).data
  // Deleting turns off and unlinks the integrations that used the account.
  await Promise.all([thunkAPI.dispatch(fetchServiceAccounts()), thunkAPI.dispatch(fetchIntegrationConfigs())])
  return result
})

export const fetchZapierProfileAndUpsertServiceAccount = createAsyncThunk(
  'serviceAccounts/fetchZapierProfile',
  async ({ accessToken, openerOrigin }: { accessToken: string; openerOrigin?: string }, thunkAPI) => {
    console.log(`Dispatching Zapier request...`)
    const state = thunkAPI.getState() as RootState
    if (state.serviceAccounts.loadingStatus !== 'loaded')
      throw new Error('Trying to load Zapier Profile before we loaded all ServiceAccounts!')

    const profile = (await integrationsApi.getZapierProfile({ headers: { authorization: `Bearer ${accessToken}` } })).data
    const existingServiceAccount = selectZapierServiceAccountByOwnerId(profile.id)(state)
    // Create Zapier ServiceAccount if a matching one doesn't exist
    if (!existingServiceAccount) {
      console.log(`Zapier ServiceAccount doesn't exist - creating one...`)
      await thunkAPI.dispatch(
        createServiceAccount({
          integrationGroupId: 'zapier',
          nickname: `${profile.email}`,
          credentials: {
            accessToken: accessToken,
            profile,
          },
        })
      )
      console.log('sending fetchServiceAccounts message to opener...')
      window.opener?.postMessage({ type: 'fetchServiceAccounts' }, openerOrigin || SENDHUB_BASE_URL)
      console.log('closing window...')
      window.close()
      return profile
    }

    console.log(`Zapier ServiceAccount already exists...`)
    if (!('profile' in existingServiceAccount.credentials)) throw new Error(`'profile' is missing from existing Zapier ServiceAccount`)

    // Only update ServiceAccount if we need to
    if (!lodash.isEqual(profile, existingServiceAccount.credentials.profile)) {
      console.log(`Updating existing Zapier ServiceAccount...`)
      await thunkAPI.dispatch(
        updateServiceAccount({
          id: existingServiceAccount.id,
          serviceAccount: {
            ...existingServiceAccount,
            nickname: `${profile.email}`,
            credentials: { ...existingServiceAccount.credentials, profile },
          },
        })
      )
    }
    console.log('sending fetchServiceAccounts message to opener...')
    window.opener?.postMessage({ type: 'fetchServiceAccounts' }, openerOrigin || SENDHUB_BASE_URL)
    console.log('closing window...')
    window.close()
    return
  }
)

export const initialServiceAccountsState: ServiceAccountsState = serviceAccountsAdapter.getInitialState({
  loadingStatus: 'not loaded',
  error: null,
})

export const serviceAccountsSlice = createSlice({
  name: SERVICE_ACCOUNTS_FEATURE_KEY,
  initialState: initialServiceAccountsState,
  reducers: {
    add: serviceAccountsAdapter.addOne,
    setMany: serviceAccountsAdapter.setMany,
    remove: serviceAccountsAdapter.removeOne,
    // ...
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchServiceAccounts.pending, (state: ServiceAccountsState) => {
        state.loadingStatus = 'loading'
      })
      .addCase(fetchServiceAccounts.fulfilled, (state: ServiceAccountsState, action: PayloadAction<ServiceAccount[]>) => {
        serviceAccountsAdapter.setAll(state, action.payload)
        state.loadingStatus = 'loaded'
      })
      .addCase(fetchServiceAccounts.rejected, (state: ServiceAccountsState, action) => {
        state.loadingStatus = 'error'
        state.error = action.error.message
      })
  },
})

/*
 * Export reducer for store configuration.
 */
export const serviceAccountsReducer = serviceAccountsSlice.reducer

/*
 * Export action creators to be dispatched. For use with the `useDispatch` hook.
 *
 * e.g.
 * ```
 * import React, { useEffect } from 'react';
 * import { useDispatch } from 'react-redux';
 *
 * // ...
 *
 * const dispatch = useDispatch();
 * useEffect(() => {
 *   dispatch(serviceAccountsActions.add({ id: 1 }))
 * }, [dispatch]);
 * ```
 *
 * See: https://react-redux.js.org/next/api/hooks#usedispatch
 */
export const serviceAccountsActions = serviceAccountsSlice.actions

/*
 * Export selectors to query state. For use with the `useSelector` hook.
 *
 * e.g.
 * ```
 * import { useSelector } from 'react-redux';
 *
 * // ...
 *
 * const entities = useSelector(selectAllServiceAccounts);
 * ```
 *
 * See: https://react-redux.js.org/next/api/hooks#useselector
 */
const { selectAll, selectEntities } = serviceAccountsAdapter.getSelectors()

export const getServiceAccountsState = (rootState: RootState): ServiceAccountsState => rootState[SERVICE_ACCOUNTS_FEATURE_KEY]

export const selectAllServiceAccounts = createSelector(getServiceAccountsState, selectAll)

export const selectZapierServiceAccountByOwnerId = (ownerId: number) =>
  createSelector(getServiceAccountsState, (state) => {
    const matchingServiceAccounts = selectAll(state).filter(
      (sA) => sA.integrationGroupId === 'zapier' && 'profile' in sA.credentials && sA.credentials.profile.id === ownerId
    )
    if (matchingServiceAccounts.length > 1)
      console.warn(`Found ${matchingServiceAccounts.length} Zapier ServiceAccounts with the profile.id of ${ownerId}!`)
    if (matchingServiceAccounts.length === 0) return undefined

    return matchingServiceAccounts[0]
  })

export const selectFirstZapierServiceAccount = createSelector(getServiceAccountsState, (state) =>
  selectAll(state).find((sA) => sA.integrationGroupId === 'zapier' && 'profile' in sA.credentials)
)
export const selectServiceAccountsError = createSelector(getServiceAccountsState, (state) => state.error)

export const selectServiceAccountsEntities = createSelector(getServiceAccountsState, selectEntities)

export const selectServiceAccountsLoadingStatus = createSelector(getServiceAccountsState, (store) => store.loadingStatus)
